// packages/api/src/PublicEventService.ts
//
// Read-only, PUBLIC client for the event website (SSR + lazy client hydration).
// It is deliberately NOT the app's management EventService/EventPageService/
// PageWidgetService: those return drafts, `rights`, and require an authenticated
// editor. This reads only PUBLISHED content via one url-driven call.
//
// SINGLE round trip, URL-driven. Pass the full incoming URL and the server
// extracts the event (host = white-label custom domain, or first path segment =
// slug) + the page (rest of the path). `initial` controls the payload:
//   • initial=true  (first landing / full SSR): event shell + published nav pages
//                    AND the requested page's content — one call, full first paint.
//   • initial=false (subsequent in-app navigation): JUST the page's content;
//                    `event` is null (the client already has the shell).
// So a 50-page site never over-fetches — each navigation pulls only its own page.
//
// Mirrors the eventsmagic-api resolver `publicEventSite(params: PublicEventSiteInput)`:
//   • the OpResult stays PURE — `{ code, errors, data: [PublicEventSite] }`
//   • `data` is ALWAYS an array — empty `[]` when nothing matched (no top-level null)
//   • the echoed request (`url`, `initial`) rides INSIDE the `PublicEventSite`
//     data item, never on the OpResult
// (The resolver is live on the subgraph; the federation gateway must be
// recomposed for it to be reachable through the gateway.)

import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { refusedNewInputs } from "./refusedNewInputs";
import { errorMessages } from "./opResultHelpers";
import { ORGANIZER_EVENT_FIELDS, PUBLIC_CAUSE_FIELD_LIST, type PublicCause, type PublicOrganizerEvent } from "./PublicOrganizerService";

/** The event "chrome" the whole site shares (header/footer/SEO/theme source).
 *  `pages` (nav) is populated on the initial call only. */
/** A2b #54 / ROADMAP B25 — the event's organizer, as its public profile shows it. Every contact fact is optional. */
export interface PublicEventOrganizer {
  id?: string | null;
  slug?: string | null;
  name?: string | null;
  summary?: string | null;
  logoUploadedFile?: { fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  website?: string | null;
  email?: string | null;
  phone?: string | null;
  socialUrls?: (string | null)[] | null;
  placeName?: string | null;
  streetAddress?: string | null;
  city?: string | null;
  stateProvince?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export interface PublicEvent {
  id: string;
  slug: string;
  title?: string | null;
  subtitle?: string | null;
  /** Who is hosting (organization / host name) — for waiver variables etc. */
  hostedBy?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  timezone?: string | null;
  eventFormat?: string | null;
  eventMode?: string | null;
  /** ROADMAP #188 — in test mode: registrations and payments made now are tests (test cards; hidden once it is live). */
  isTestMode?: boolean | null;
  venueId?: string | null;
  onlineMeetingUrl?: string | null;
  coverUploadedFileId?: string | null;
  /** Cover image URL when the host pasted a URL (else use `coverUploadedFile.fileUrl`). */
  coverUrl?: string | null;
  /** Cover video URL when the host pasted a URL (else use `coverVideoUploadedFile.fileUrl`). */
  coverVideoUrl?: string | null;
  /** Cover image file resolved from ms_storage (when the host picked a stored file). */
  coverUploadedFile?: PublicUploadedFile | null;
  /** Cover video file resolved from ms_storage (when the host picked a stored file). */
  coverVideoUploadedFile?: PublicUploadedFile | null;
  isListed?: boolean | null;
  /**
   * A2e #81 — may search engines index this event's pages (the owner's plan includes `search_indexing`)? Absent or
   * null — a gateway not yet recomposed, or any doubt — means NO: the site sends `noindex, nofollow`.
   */
  searchIndexable?: boolean | null;
  /**
   * A2e #96 — the owner's plan removes our branding (Business): leave out the footer promo ("Powered by …", "Create your
   * event"), never the stock photo credits. Absent (an older gateway) = false = the promo shows — the safe side.
   */
  hidePlatformBranding?: boolean | null;
  /**
   * A2b #54 / ROADMAP B25 — who is hosting this: their public organizer profile (null without one, or for a draft), with
   * the contact facts that profile publishes on `/o/<slug>` — for the organizer footer block.
   */
  organizer?: PublicEventOrganizer | null;
  /**
   * A2e #97 — the custom CSS for this event's pages: the account's sheet, then the event's own, already checked by the
   * server (no `</style`, no @import, pictures only from the host's uploads) and left out when the plan lacks advanced
   * styling. Absent (an older gateway) = none.
   */
  customCss?: string | null;
  /**
   * Guests-only pages (2026-10-03) — some published page is kept for guests (it may not be in this menu at all). The
   * browser asks again with its visitor id only then, or when the page held blocks back.
   */
  hasRestrictedPages?: boolean | null;
  /**
   * The event's free places (2026-10-23) — the hero says "Only 7 places left" when they run low (`@em/blocks`
   * `placesLowText`). Counted as the checkout and the RSVP form count them. null = uncapped, or a recurring event.
   */
  placesLeft?: number | null;
  /**
   * 2026-10-04 — an invite-only event's PRIVATE answer: this viewer is not on its guest list, so only the name and theme
   * came back (no details, menu or pages). The site shows the private notice and asks again once it knows the visitor.
   */
  privateSite?: boolean | null;
  /**
   * ROADMAP A2e #92 — when what the site shows may next change by the TIME (a date starts or ends), when some page or block
   * is kept for before / during / after the event. Null otherwise, and for the crew. The site reloads the page then.
   */
  visibilityChangesAt?: string | null;
  /** Who may see the venue: everyone | invited | attending. */
  locationVisibility?: string | null;
  /** The event's venue — present ONLY when the viewer may see it (server-gated). */
  location?: PublicEventLocation | null;
  /** Published pages nav (routing table for the menu). Present when initial=true. */
  pages?: PublicEventPageMeta[] | null;
  /** True when the current (authenticated) viewer may manage this event — owner
   *  or crew. Server-computed; drives the "Manage event" affordance. Absent/false
   *  for anonymous viewers. */
  viewerCanManage?: boolean | null;
  /** The event's highlight badges. Present when initial=true. */
  highlights?: PublicEventHighlight[] | null;
  /** The event's website theme (effective design tokens). Null = the site uses its default. Present when initial=true. */
  theme?: PublicEventTheme | null;
  /** How the host wants the cover hero presented. Always present (server fills safe defaults). */
  hero?: PublicEventHero | null;
  /** Custom label for the primary register/RSVP button, or null to use the site default. */
  rsvpButtonLabel?: string | null;
  /** Event-wide DM mode ('all' | 'groups'); null = off. Drives the Messages launcher on every page. */
  dmMode?: string | null;
  /**
   * A2b #56 — set ONLY when the host has CANCELLED this event; null/absent on every live one, so a
   * falsy check is the whole test. ⚠ Nested so it cannot half-exist: a reason with no cancelled
   * flag is the shape that renders host copy nobody sees.
   */
  cancellation?: PublicEventCancellation | null;
  /**
   * #263 — the event is OVER (the host marked it completed, or a one-off event's end has passed — the server's
   * `eventHasEnded`): no reply, nothing sold; a guest is offered their own RSVP / orders instead of Register / Get tickets.
   */
  ended?: boolean | null;
  /** Last day to RSVP / buy tickets (ISO UTC), or null for no deadline. */
  rsvpDeadline?: string | null;
  /** Days before the deadline to start showing the "closing soon" reminder (default 7). */
  rsvpReminderDays?: number | null;
  /** #41: when the door opens for this event's own start (ISO UTC). Null on a recurring event —
   *  its dates each answer it on `upcomingDates` — and null when the event has no start. */
  doorsOpenAt?: string | null;
  /** single | recurring | agenda. */
  scheduleMode?: string | null;
  /** ROADMAP A4: a recurring event's next few dates still to come (null on any other event). */
  upcomingDates?: PublicEventDate[] | null;
  /** ROADMAP A4: how many dates are still to come in total. */
  upcomingDatesTotal?: number | null;
  /** ROADMAP A3: host-authored guest-visible copy — pre-action form copy (surface form_pre) and
   *  post-action website copy by status (surface site_post). Index by (surface, trigger, statusKey).
   *  The email-replacement surface is server-only and NOT included here. */
  messageCopy?: PublicEventCopy[] | null;
}

/** ROADMAP A3: one piece of host-authored guest-visible copy. Sanitize `bodyHtml` before rendering. */
export interface PublicEventCopy {
  /** form_pre | site_post. */
  surface?: string | null;
  /** rsvp | ticket. */
  trigger?: string | null;
  /** Narrows site_post by status (approved/maybe/declined/pending/waitlisted | ticket_purchased); null for form copy. */
  statusKey?: string | null;
  /** The authored copy (HTML). */
  bodyHtml?: string | null;
}

/** Host-chosen presentation for the cover hero (from event.settings.hero). */
/**
 * A2b #56 — this event has been called off. Present ONLY on a cancelled event.
 *
 * ⚠ The site keeps serving the page rather than 404ing: somebody holding a ticket has to be able to
 * find out what happened and reach their own order.
 */
export interface PublicEventCancellation {
  /** When the host cancelled it (ISO UTC). */
  canceledAt?: string | null;
  /** The host's own words, or null. ⚠ The page says the event is off WITH OR WITHOUT this. */
  reason?: string | null;
}

export interface PublicEventHero {
  /** Size the cover to (nearly) the full viewport height (minus the header). */
  fullScreen?: boolean | null;
  /** Show the event identity text (title / subtitle / date-time / location). Default true. */
  showDetails?: boolean | null;
  /** Cover video: autoplay (unmuted autoplay may be blocked by the browser). */
  videoAutoplay?: boolean | null;
  /** Cover video: start muted. Default true. */
  videoMuted?: boolean | null;
  /** Cover video: loop playback. */
  videoLoop?: boolean | null;
  /** B18 — how the hero arrives: "none" | "rise" | "highlight" | "spray" (absent from an older gateway = none). */
  entrance?: string | null;
  /** B19 — the main RSVP / Buy button nudges now and then. */
  ctaNudge?: boolean | null;
  /**
   * B18 — the page's animated background: 'theme' (follow the theme's `effects.page`), 'none', or an effect (the API's
   * `HERO_PAGE_EFFECT`; the site's list is `PAGE_EFFECTS`). Absent from an older gateway = 'theme'. ⓘ Every motion here is
   * off on a plan without advanced styling — the server sends 'none' / false.
   */
  pageEffect?: string | null;
  /**
   * B18 round 2 — how the cover picture arrives: 'auto' (follow `entrance`), 'none', 'zoom', 'focus', 'tv', 'newspaper',
   * 'flip', 'polaroid', 'iris'. Absent from an older gateway = 'auto'.
   */
  coverEntrance?: string | null;
  /**
   * B18 round 3 — the opening scene over the whole site on the event's home page, opened by the guest's tap: 'none',
   * 'envelope', 'curtains', 'wooden_doors', 'metal_doors', 'portal', 'gift', 'gates', 'frost', 'balloons',
   * 'christmas_tree', 'ornaments', 'sleigh', 'midnight' (the site's
   * list is `OPENERS`). Absent from an older gateway = 'none'.
   */
  opener?: string | null;
}

/** Design tokens (schema v2). See eventsmagic-api docs/THEMES.md for the vocabulary. */
export interface PublicThemeTokens {
  version?: number;
  mode?: "light" | "dark";
  /** `headingSize` (2026-10-25): every heading's size, a step of the blocks' text-size scale ('sm' … 'xxl'); absent = 'md'. */
  font?: { display?: string; body?: string; baseSize?: string; scale?: number; headingSize?: string };
  color?: {
    background?: string;
    surface?: string;
    surfaceAlt?: string;
    ink?: string;
    inkSoft?: string;
    muted?: string;
    line?: string;
    lineStrong?: string;
    accent?: string;
    accentInk?: string;
    accentWash?: string;
    accentLine?: string;
    success?: string;
    successWash?: string;
    info?: string;
    infoWash?: string;
  };
  shape?: { radius?: string; shadow?: string };
  button?: {
    primary?: { bg?: string; text?: string };
    secondary?: { bg?: string; text?: string; border?: string };
  };
  /** Element/role overrides. `block` (A2e #88) = every content block's background + text colour (absent = the site's). */
  element?: Record<string, Record<string, string>> & { block?: { bg?: string | null; text?: string | null } };
  /** `page`: the theme's page effect; `media` (2026-10-24): 'grayscale' draws every picture and video in black and white. */
  effects?: { page?: string; media?: 'color' | 'grayscale' | string };
  /**
   * A2e #88 — background images behind the whole site and behind the content area. Premium: the server leaves them out
   * for a plan without `site_styling` (while paywalls are on). Shape mirrored by @em/blocks `backgroundImage.ts`.
   */
  background?: { site?: PublicBackgroundImage | null; content?: PublicBackgroundImage | null };
}

/** A2e #88 — one background image (site, content area, or a block's `config.backgroundImage`). */
export interface PublicBackgroundImage {
  url?: string | null;
  uploadedFileId?: string | null;
  /** 0–100: the image over the colour beneath it (absent = 100). */
  opacity?: number | null;
  /** A CSS background position keyword pair: center, top, bottom left, … (absent = center). */
  position?: string | null;
}

/** The event's effective website theme: merged design tokens + per-event custom CSS. */
export interface PublicEventTheme {
  id?: string | null;
  name?: string | null;
  mode?: string | null;
  tokens?: PublicThemeTokens | null;
  customCss?: string | null;
}

/** An ms_storage file resolved via federation — select fileUrl / thumbnailUrl. */
export interface PublicUploadedFile {
  fileUrl?: string | null;
  thumbnailUrl?: string | null;
}

/** The event's venue for the public site (already access-gated by the server). */
export interface PublicEventLocation {
  venueId?: string | null;
  name?: string | null;
  /** A pre-formatted single-line address, or null. */
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  phone?: string | null;
  website?: string | null;
  /** A Google Maps directions link (coords when available, else the address). */
  directionsUrl?: string | null;
}

/** One highlight badge shown on the event site. */
export interface PublicEventHighlight {
  id: string;
  label?: string | null;
  /** A registered icon name, or null. */
  icon?: string | null;
  /** Catalog group (amenity/food_drink/entertainment/…) — drives the badge colour. */
  highlightGroup?: string | null;
  orderNo?: number | null;
}

/** One page's routing + nav + SEO metadata — NO content. */
export interface PublicEventPageMeta {
  id: string;
  name?: string | null;
  /** The route segment on the event site (e.g. "schedule", "travel"). */
  path?: string | null;
  icon?: string | null;
  orderNo?: number | null;
  showInNav?: boolean | null;
  isHome?: boolean | null;
  accessLevel?: string | null;
  /**
   * Guests-only pages (2026-10-03) — closed to the visitor this read was made for: its blocks (and its own SEO text) were
   * not sent. The site shows a "for guests" notice and asks again with the visitor id once it knows it. Absent on a
   * gateway that has not been restarted since (read as false — the server still sends no blocks).
   */
  locked?: boolean | null;
  /** How many of this page's blocks were held back from that visitor. */
  hiddenBlocks?: number | null;
  /**
   * ROADMAP A2e #92 — when this page, kept for one part of the event, stops being shown: the site takes it away then,
   * without a reload. Null when it is shown always (or for the crew). A page out of its time is not sent at all.
   */
  closesAt?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  seoImageUploadedFileId?: string | null;
}

/** A single content block on a page. `config`/`settings` are JSON. */
export interface PublicPageWidget {
  id: string;
  eventPageId?: string | null;
  eventId?: string | null;
  widgetType?: string | null;
  title?: string | null;
  content?: string | null;
  config?: Record<string, any> | null;
  settings?: Record<string, any> | null;
  accessLevel?: string | null;
  orderNo?: number | null;
  /** ROADMAP A2e #92 — when this block, kept for one part of the event, stops being shown (null: always / the crew). */
  closesAt?: string | null;
}

/** ROADMAP A4: one upcoming date of a recurring event. */
export interface PublicEventDate {
  id: string;
  name?: string | null;
  /** Its name, else its start in the event's timezone. */
  label?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  /** #41: when this date's door opens (ISO UTC), resolved server-side (date → event → default). */
  doorsOpenAt?: string | null;
}

/** One schedule time slot (public-safe). */
export interface PublicEventTimeSlot {
  id: string;
  name?: string | null;
  description?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  venueName?: string | null;
  venueAddress?: string | null;
  venuePhone?: string | null;
  venueWebsite?: string | null;
  venueDirectionsUrl?: string | null;
  /** How `description` is authored: richtext | markdown | html | plain. Absent from a gateway that predates it. */
  descriptionFormat?: string | null;
  /** The event team members shown on this session, in the host's order (public-safe, like the guests block). */
  speakers?: PublicEventGuest[] | null;
  /** The booked acts playing it (#70), in the host's order — the lineup's shape. Absent from a gateway that predates it. */
  acts?: PublicEventArtist[] | null;
  /** The session's track / room ("Main Hall"); null = for everyone. Absent from a gateway that predates it. */
  track?: string | null;
}

/** A session a team member is shown on (#70) — "speaking at 10:00, Main Hall" on their card. */
export interface PublicEventGuestSession {
  id: string;
  name?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  isAllDay?: boolean | null;
  track?: string | null;
}

/** One guest (public-safe — display data only, no email/phone). */
export interface PublicEventGuest {
  id: string;
  /** Effective display name (guest's, else the linked contact's). */
  name?: string | null;
  /** guest | host | co_host | organizer | speaker | … */
  guestRole?: string | null;
  rsvpStatus?: string | null;
  bio?: string | null;
  website?: string | null;
  socialLinks?: (string | null)[] | null;
  /** Uploaded avatar resolved from ms_storage. */
  avatarUploadedFile?: PublicUploadedFile | null;
  /** Gravatar URL computed server-side from the guest's email (email never exposed). */
  gravatarUrl?: string | null;
  /** Guest-group ids this guest belongs to (drives the guests block's `group` source). */
  groupIds?: (string | null)[] | null;
  /** Effective job title / company (the guest's event-specific value, else the contact's) — "Head of Design, Acme". */
  jobTitle?: string | null;
  company?: string | null;
  /** The sessions this team member is shown on (#70), earliest first. Absent from a gateway that predates it. */
  sessions?: PublicEventGuestSession[] | null;
}

/** One image reference — a stored URL and/or an ms_storage file. */
export interface PublicMediaImage {
  url?: string | null;
  uploadedFile?: PublicUploadedFile | null;
}

/** One point of interest (public-safe). */
export interface PublicEventPoi {
  id: string;
  name?: string | null;
  description?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  icon?: string | null;
  color?: string | null;
  address?: string | null;
  link?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  price?: number | null;
  currency?: string | null;
  priceUnit?: string | null;
  /** How many guest parties have upvoted this place. */
  upvoteCount?: number | null;
}

/** One accommodation / lodging option (public-safe). */
export interface PublicEventAccommodation {
  id: string;
  name?: string | null;
  description?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  icon?: string | null;
  color?: string | null;
  images?: PublicMediaImage[] | null;
  address?: string | null;
  /** Straight-line distance from the event venue, in meters (the FE formats it). */
  distanceMeters?: number | null;
  link?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  price?: number | null;
  currency?: string | null;
  priceUnit?: string | null;
  groupBookingCode?: string | null;
  /** How many guest parties have upvoted this stay. */
  upvoteCount?: number | null;
}

/** One travel leg (public-safe). Raw UTC times + per-leg timezones — the FE formats. */
export interface PublicEventTravelSegment {
  id: string;
  travelMode?: string | null;
  travelStatus?: string | null;
  direction?: string | null;
  departsAt?: string | null;
  arrivesAt?: string | null;
  fromCode?: string | null;
  fromLabel?: string | null;
  fromTimezone?: string | null;
  toCode?: string | null;
  toLabel?: string | null;
  toTimezone?: string | null;
  carrier?: string | null;
  serviceNumber?: string | null;
  /** Travellers on this leg (name + seat). Empty unless a travel block opted in. */
  travellers?: PublicEventTraveller[] | null;
}

/** One traveller on a leg (public-safe — name + seat only). */
export interface PublicEventTraveller {
  name?: string | null;
  seat?: string | null;
}

/** One session at a map element. */
export interface PublicEventMapSession {
  startsAt?: string | null;
  title?: string | null;
  track?: string | null;
}

/** One on-site map element (public-safe). */
export interface PublicEventMapElement {
  id: string;
  name?: string | null;
  description?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  icon?: string | null;
  color?: string | null;
  hoursText?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  sessions?: PublicEventMapSession[] | null;
}

/** One signup-list item (public-safe). */
export interface PublicEventSignupItem {
  id: string;
  name?: string | null;
  description?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  icon?: string | null;
  color?: string | null;
  images?: PublicMediaImage[] | null;
  link?: string | null;
  price?: number | null;
  currency?: string | null;
  priceUnit?: string | null;
  quantityAvailable?: number | null;
  maxQuantityPerGuest?: number | null;
  /** How many guest parties have upvoted this item. */
  upvoteCount?: number | null;
  /** #145 — who signed up for it; null unless a block showing it lets guests see everyone's sign-ups. */
  signups?: PublicSignupItemSignup[] | null;
  /** #149 — guests PAY for it online through the site's cart (the server decides; the free sign-up refuses it). */
  soldOnline?: boolean | null;
  /** How many are taken (everyone's sign-ups + places held in carts) — counts only. Absent on an older gateway. */
  quantityTaken?: number | null;
}

/** #145 — one guest's sign-up for a sign-up list item, as the public may see it (no contact details, no ids). */
export interface PublicSignupItemSignup {
  /** The same guest across items within ONE response (g1, g2 …) — never their id. */
  guestKey?: string | null;
  name?: string | null;
  quantity?: number | null;
  note?: string | null;
}

/** One answer option on a poll/quiz question. `isCorrect` is NOT exposed publicly (it would
 *  leak quiz answers) — correctness is revealed post-answer via publicPollResults. */
export interface PublicPollOption {
  /** Option id — needed to record a guest's answer (selectedOptionIds). */
  id?: string | null;
  label?: string | null;
}

/** One poll / quiz question (public-safe). */
export interface PublicEventPollQuestion {
  id: string;
  question?: string | null;
  description?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  icon?: string | null;
  color?: string | null;
  questionType?: string | null;
  options?: PublicPollOption[] | null;
  isQuiz?: boolean | null;
  allowAddOption?: boolean | null;
  isRequired?: boolean | null;
  images?: PublicMediaImage[] | null;
}

/** One price rule on a ticket type (the resolver applies it to the base price). */
export interface PublicTicketPriceRule {
  ruleKind?: string | null;
  effectKind?: string | null;
  amount?: number | null;
  orderNo?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  membershipTypeId?: string | null;
  /** Resolved membership name for a membership-kind rule, or null. */
  membershipName?: string | null;
}

/** One ticket type / add-on (public-safe, raw-ish — the resolver prices it). */
export interface PublicEventTicket {
  id: string;
  name?: string | null;
  /** any | optional | required — required = members only. */
  membershipMode?: string | null;
  /** The membership types a members-only ticket accepts, by name. */
  membershipTypeNames?: string[] | null;
  description?: string | null;
  isAddOn?: boolean | null;
  /** Base / fallback price (0 = free). */
  price?: number | null;
  currency?: string | null;
  isPayWhatYouWant?: boolean | null;
  minPrice?: number | null;
  maxPrice?: number | null;
  quantityTotal?: number | null;
  /** This type's OWN stock left (counted as the checkout counts); null = unlimited, or a recurring event (per date). The
   *  room is the page data's `placesLeft`. */
  available?: number | null;
  /** Nothing of it can be bought now — its stock is gone, or (an admission) the room is full. */
  soldOut?: boolean | null;
  salesStartsAt?: string | null;
  salesEndsAt?: string | null;
  /** on_sale | door_only | off. */
  salesMode?: string | null;
  orderNo?: number | null;
  priceRules?: PublicTicketPriceRule[] | null;
}

/** Live event headcounts for the stats block's live sources. */
export interface PublicStatCounts {
  attending?: number | null;
  guests?: number | null;
}

/** One budget line (public-safe). Expected/paid/remaining are derived server-side. */
export interface PublicEventBudgetItem {
  id: string;
  name?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  icon?: string | null;
  color?: string | null;
  calcType?: string | null;
  expected?: number | null;
  paid?: number | null;
  remaining?: number | null;
  /** Guest-pledged toward this line (intent; separate from paid). */
  pledged?: number | null;
  dueDate?: string | null;
  responsibleLabel?: string | null;
  currency?: string | null;
}

/** One task / appointment (public-safe display subset). */
export interface PublicEventTask {
  id: string;
  title?: string | null;
  taskType?: string | null;
  description?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  icon?: string | null;
  color?: string | null;
  scheduledAt?: string | null;
  location?: string | null;
  taskStatus?: string | null;
  expectedDurationMin?: number | null;
  actualDurationSec?: number | null;
  responsibleKind?: string | null;
  responsibleLabel?: string | null;
}

/** One partner / sponsor (public-safe showcase shape). */
export interface PublicEventPartner {
  id: string;
  name?: string | null;
  tierLabel?: string | null;
  kind?: string | null;
  partnerPackageId?: string | null;
  /** Showcase logo resolved from ms_storage (select fileUrl). */
  logoUploadedFile?: PublicUploadedFile | null;
  /** Gravatar fallback for a person-backed partner with no logo (server-computed). */
  logoGravatarUrl?: string | null;
  /** Website with the partner's UTM params already appended. */
  websiteUrl?: string | null;
  /** Social links (from a linked person) with UTM appended. */
  socials?: (string | null)[] | null;
}

/**
 * One sponsorship package OFFERED on the event (#A4.5) — what is still available to buy.
 *
 * ⚠ The MIRROR of PublicEventPartner, not a variant of it: that is a sponsor who has already
 * signed, this is a package still on the table. The server has already dropped drafts, resolved the
 * per-event price and reduced slots to how many are LEFT — the raw taken-count is never published.
 */
/** A reward a volunteer earns for a shift — issued when they CHECK IN (#A4.6). */
export interface PublicVolunteerShiftReward {
  name?: string | null;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  quantity?: number | null;
}

/**
 * A volunteer shift the public may sign up for (#A4.6). ⚠ Never carries the host's INSTRUCTIONS — those reach
 * a volunteer only once a shift is confirmed.
 */
export interface PublicVolunteerShift {
  id: string;
  name?: string | null;
  volunteerRoleId?: string | null;
  roleName?: string | null;
  /** The role's public pitch / duties / eligibility (markdown), from the account's role catalog. */
  roleSummary?: string | null;
  roleResponsibilities?: string | null;
  roleEligibility?: string | null;
  roleColor?: string | null;
  roleIcon?: string | null;
  /** ISO UTC; null = untimed. */
  startsAt?: string | null;
  endsAt?: string | null;
  locationKind?: string | null;
  locationLabel?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** 0 = no stated cap. */
  quantityNeeded?: number | null;
  /** Places still open; null when there is no cap. */
  spotsLeft?: number | null;
  isFull?: boolean | null;
  /** review | auto. */
  approvalMode?: string | null;
  applicationsCloseAt?: string | null;
  rewards?: PublicVolunteerShiftReward[] | null;
}

export interface PublicEventPartnerPackage {
  /** The OFFERING id — what an application is made against. */
  id: string;
  partnerPackageId?: string | null;
  name?: string | null;
  /** sponsor | partner | media_partner | exhibitor | … — the grouping key. */
  partnerKind?: string | null;
  /** What the host wrote FOR applicants. Never the internal catalog description. */
  publicSummary?: string | null;
  /** How `publicSummary` is authored: richtext | markdown | html | plain. Render it with `renderPolicyHtml`. */
  publicSummaryFormat?: string | null;
  /** What the package includes (jsonb upstream, so it can arrive null or non-array). */
  benefits?: unknown;
  /** What the HOST is owed for this event, or null when free / unpriced. */
  amount?: number | null;
  /** Our platform service fee on it. */
  serviceFeeAmount?: number | null;
  /** ⚠ What the SPONSOR actually pays — larger than `amount` when the host passes fees on. Quote THIS. */
  /** Our service fee, as far as the sponsor pays it. Null: the charge cannot be split. */
  serviceFeeToSponsor?: number | null;
  /** The card processing fee, as far as the sponsor pays it. */
  cardFeeToSponsor?: number | null;
  totalAmount?: number | null;
  /** absorb | service | service_processor. */
  feeMode?: string | null;
  currencyCode?: string | null;
  /** Places left, or null when unlimited. Never negative. */
  slotsLeft?: number | null;
  isFull?: boolean | null;
  isOpenForApplications?: boolean | null;
  /** ISO-8601, or null for "until the event". */
  applicationsCloseAt?: string | null;
  /** at_application | on_approval | offline — what the CTA can promise about money. */
  paymentMode?: string | null;
}

/** One booked artist / lineup act (public-safe showcase shape). Rows arrive already
 *  sorted by billingIndex then name. */
export interface PublicEventArtist {
  id: string;
  name?: string | null;
  /** Catalog artist kind (dj/band/speaker/…) — for an optional grouping/label. */
  kind?: string | null;
  /** Billing tier (headliner/co_headline/support/opener/guest), or null. */
  billing?: string | null;
  /** Lineup sort order within a tier. */
  billingIndex?: number | null;
  /** Public lineup text shown on the event website. */
  blurb?: string | null;
  /** Showcase photo resolved from ms_storage (select fileUrl / thumbnailUrl). */
  photoUploadedFile?: PublicUploadedFile | null;
  website?: string | null;
  spotifyUrl?: string | null;
  youtubeUrl?: string | null;
  /** Social links (from the linked catalog artist). */
  socials?: (string | null)[] | null;
  /** Set time start / end (UTC ISO), or null. The FE formats in the event timezone. */
  setTimeStartsAt?: string | null;
  setTimeEndsAt?: string | null;
  /** Stage / room the act plays, or null. */
  stage?: string | null;
  /** #165 — the sessions it plays that are not over yet, in time order (a recurring event's dates). Empty on a session's act list. */
  sets?: PublicEventArtistSet[] | null;
}

/** #165 — one upcoming session an act plays. */
export interface PublicEventArtistSet {
  slotId?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  stage?: string | null;
}

/** One winner of a published prize draw (public-safe: a snapshotted name + prize label). */
export interface PublicPrizeDrawWinner {
  displayName?: string | null;
  prize?: string | null;
  orderNo?: number | null;
}

/** One published prize-draw round for the prize-winners block. Carries winners once drawn,
 *  or a pending message before. */
export interface PublicPrizeDraw {
  id: string;
  name?: string | null;
  description?: string | null;
  drawn?: boolean | null;
  pendingMessage?: string | null;
  winners?: PublicPrizeDrawWinner[] | null;
}

/** One team's standing in a live game scoreboard (#34). */
export interface PublicGameScoreboardTeam {
  team?: string | null;
  inPlay?: number | null;
  out?: number | null;
  total?: number | null;
}

/** One eliminated (revealed) player in a live game scoreboard. */
export interface PublicGameScoreboardElimination {
  displayName?: string | null;
  markerLabel?: string | null;
  markerColor?: string | null;
  team?: string | null;
  outcome?: string | null;
}

/** One player's row in a checklist-game leaderboard (Human Bingo / scavenger). */
export interface PublicGameLeaderboardEntry {
  displayName?: string | null;
  done?: number | null;
  total?: number | null;
  complete?: boolean | null;
  rank?: number | null;
  /** Who-Am-I: the player's now-revealed persona (the identity they guessed). */
  identityName?: string | null;
}

/** A party game's latest-round PUBLIC scoreboard for the game-scoreboard block.
 *  Only revealed roles appear — a still-secret marker is never included. */
export interface PublicGameScoreboard {
  gameId: string;
  gameName?: string | null;
  roundNo?: number | null;
  total?: number | null;
  inPlay?: number | null;
  out?: number | null;
  teams?: PublicGameScoreboardTeam[] | null;
  eliminations?: PublicGameScoreboardElimination[] | null;
  /** Self-mark games (Human Bingo / scavenger): render the leaderboard instead of teams. */
  checklist?: boolean | null;
  /** Pair games (Networking Roulette): render the connections-made leaderboard or the pairings board. */
  networking?: boolean | null;
  /** Who-Am-I: render the solvers leaderboard (name + persona + guess count + place). */
  whoami?: boolean | null;
  leaderboard?: PublicGameLeaderboardEntry[] | null;
  /** Pair games: THIS round's pairings, for the host-opt-in "who's meeting whom" board. */
  pairs?: PublicGameScoreboardPair[] | null;
}

/** One Networking Roulette pairing — the guests matched together this round (2, or 3 for an odd count). */
export interface PublicGameScoreboardPair {
  members?: (string | null)[] | null;
}

/** One race-timing row (public-safe, raw signals — the resolver derives state/place/time). */
export interface PublicEventRaceResult {
  id: string;
  bibNumber?: string | null;
  participantName?: string | null;
  groupRef?: string | null;
  groupLabel?: string | null;
  startedAt?: string | null;
  quitAt?: string | null;
  durationMs?: number | null;
}

/** Per-page raw entities the page's blocks need. A collection is null when no block
 *  on the page needs it (vs [] = needed but none exist). */
export interface PublicPageData {
  timeSlots?: PublicEventTimeSlot[] | null;
  guests?: PublicEventGuest[] | null;
  /**
   * #243 c — set HERE (never by the server) when the read asked the API to leave the guests out (`deferGuests`) and it
   * did: `guests` holds the crew only, and the guests arrive with the browser's own read after load. Until then a
   * guests block shows that they are coming — never "No one to show yet".
   */
  guestsDeferred?: boolean | null;
  /** #243 c — set by the site when the browser's read of the deferred guests FAILED: the block says so (never "No one"). */
  guestsReadFailed?: boolean | null;
  pois?: PublicEventPoi[] | null;
  accommodations?: PublicEventAccommodation[] | null;
  travel?: PublicEventTravelSegment[] | null;
  mapElements?: PublicEventMapElement[] | null;
  signupItems?: PublicEventSignupItem[] | null;
  pollQuestions?: PublicEventPollQuestion[] | null;
  tickets?: PublicEventTicket[] | null;
  /** The event's free places, with the tickets — the room every admission type shares (null = uncapped / recurring). */
  placesLeft?: number | null;
  statCounts?: PublicStatCounts | null;
  raceResults?: PublicEventRaceResult[] | null;
  budget?: PublicEventBudgetItem[] | null;
  budgetCurrency?: string | null;
  tasks?: PublicEventTask[] | null;
  partners?: PublicEventPartner[] | null;
  /** Sponsorship packages OFFERED (present when the page has a partner-packages block). */
  partnerPackages?: PublicEventPartnerPackage[] | null;
  /** Volunteer shifts OPEN for public sign-up (present when the page has a volunteer-shifts block, #A4.6). */
  volunteerShifts?: PublicVolunteerShift[] | null;
  artists?: PublicEventArtist[] | null;
  prizeDraws?: PublicPrizeDraw[] | null;
  gameScoreboards?: PublicGameScoreboard[] | null;
  /** ROADMAP B26 — the organizer's other UPCOMING events (present when the page has an organizer-events block). */
  organizerEvents?: PublicOrganizerEvent[] | null;
  /** …and THIS event's own channel (null without one), for a block showing only the events of the same channel. */
  organizerChannel?: string | null;
  /**
   * #204 step 4 — the causes the event collects for, in the event's order (present when the page has an OPEN Donate or
   * Goal block; empty otherwise). Each says whether its window is open (`takesGifts`). Absent from an older gateway.
   */
  causes?: (PublicCause | null)[] | null;
  /** #204 step 4 — gifts can be TAKEN on this event now (its plan and a card): a Donate block offers Give only then. */
  causesCanGive?: boolean | null;
  /** #204 step 4 — gifts here take TEST cards (the event is in test mode): the Donate block and its drawer say so. */
  causesTestMode?: boolean | null;
}

/** The resolved page: its metadata + published widgets + the entities its blocks need. */
export interface PublicPage {
  meta: PublicEventPageMeta | null;
  widgets: PublicPageWidget[];
  data?: PublicPageData | null;
}

/** One event-site render payload = one `data` item. The request is echoed here
 *  (url/initial) so the OpResult keeps its plain code/errors/data shape. */
export interface PublicEventSite {
  /** Echo of the requested url. */
  url: string;
  /** Echo of the `initial` flag. */
  initial: boolean;
  /** Event shell + nav pages. Present only when `initial` was true. */
  event: PublicEvent | null;
  page: PublicPage | null;
  /** #62 P6(d4) — a DRAFT opened with a crew preview token. Only asked for on a preview read (absent otherwise). */
  isPreview?: boolean | null;
  /**
   * Guests-only pages — read for the CREW (a preview token for this event — on a live event too): every page and block is
   * open, and the site must not re-ask as a guest. Only asked for on a preview read, under the gateway retry.
   */
  crewView?: boolean | null;
  /** The menu for the visitor the read named (`visitorId`): the pages they may open + the guests-only ones locked. */
  pages?: PublicEventPageMeta[] | null;
}

export interface PublicEventSiteArgs {
  /** The full incoming URL (host + path + search). */
  url: string;
  /** true = first landing (event + page); false = in-app nav (page only). */
  initial: boolean;
  /** #62 P6(d4) — a crew preview token (`eventPreviewLink`). Without one, a DRAFT event is not found. */
  preview?: string | null;
  /**
   * Guests-only pages — the browser's visitor id. With it, the pages and blocks that visitor's guests may see come back,
   * and `pages` is their menu. ⚠ The SERVER render never sends it (SSR stays visitor-agnostic); only the browser does.
   */
  visitorId?: string | null;
  /**
   * 2026-10-04 — a guest's personal invitation token (`?inv=`). Its holder views the site as that guest; on an
   * invite-only event it is the way in. Sent only when there is one (a gateway that predates it refuses the field).
   */
  inviteToken?: string | null;
  /**
   * #243 c — leave the guests out of the people blocks (the crew stays); the browser reads them after load. Honoured only
   * on a plain anonymous read (no preview, visitor or invitation — those are never re-read for guests), and dropped by
   * its own retry on a gateway that predates it (then the guests come as before).
   */
  deferGuests?: boolean | null;
}

/** Optional per-call transport tuning (fetch policy, extra Apollo options). */
export interface PublicReadOptions {
  fetchPolicy?: string;
  [key: string]: any;
}

const EVENT_SHELL_FIELDS = `
  id
  slug
  title
  subtitle
  hostedBy
  startsAt
  endsAt
  isAllDay
  timezone
  eventFormat
  eventMode
  isTestMode
  venueId
  onlineMeetingUrl
  coverUploadedFileId
  coverUrl
  coverVideoUrl
  coverUploadedFile { fileUrl thumbnailUrl }
  coverVideoUploadedFile { fileUrl }
  isListed
  viewerCanManage
  locationVisibility
  location {
    venueId
    name
    address
    latitude
    longitude
    phone
    website
    directionsUrl
  }
  cancellation { canceledAt reason }
  ended
`;

const HIGHLIGHT_FIELDS = `
  id
  label
  icon
  highlightGroup
  orderNo
`;

const PAGE_NAV_FIELDS = `
  id
  name
  path
  icon
  orderNo
  showInNav
  isHome
  accessLevel
  seoTitle
  seoDescription
  seoImageUploadedFileId
`;

/** Guests-only pages — asked for under the same retry as the schedule's people (an older gateway refuses them). */
const PAGE_ACCESS_FIELDS = `
  locked
  hiddenBlocks
`;

/**
 * ROADMAP A2e #92 — when a page / block kept for a time closes (on the page meta, the menu's pages and the blocks). Asked
 * for under a retry of their OWN (`refusedWhenFields`): a gateway that does not know them yet costs taking things away at
 * a boundary without a reload, never the guests-only fields above (or the visitor's re-ask, which has no base shape).
 */
const PAGE_WHEN_FIELDS = `
  closesAt
`;

const WIDGET_FIELDS = `
  id
  eventPageId
  eventId
  widgetType
  title
  content
  config
  settings
  accessLevel
  orderNo
`;

// ⚠⚠ TWO SHAPES, and the ordinary one is exactly what it was (#62 P6(d4)). A gateway that has not been recomposed
// rejects a WHOLE query naming an unknown argument or field — and this query is the entire event website. So only a
// crew PREVIEW (a draft, opened with a token) sends `preview` and asks for `isPreview`; every guest's read keeps the old
// shape and cannot go dark with a deploy in the wrong order.
//
// ⚠ And the schedule's people, description format (2026-09-30), track (2026-10-01), a session's acts and a team member's
//   sessions (#70) are asked for, with ONE retry without them when the gateway refuses exactly those fields
//   (`refusedSlotPeople`) — so a gateway not yet recomposed costs the speakers and the formatting, never the website.
//   A2e #81's `searchIndexable` rides the same retry: refused, it is absent, and absent means noindex — the safe side.
//   The guests-only `locked` / `hiddenBlocks` ride it too: refused, they are absent — and the SERVER still sent no blocks
//   for a closed page, so an old gateway costs the "for guests" notice, never the privacy. #92's `closesAt` /
//   `visibilityChangesAt` are asked for under a retry of their own (`refusedWhenFields`) BEFORE that one, so an older
//   gateway drops only them: a page left open then changes on its next load (the server already left out what is closed).
//   #145's sign-up list `signups` (who signed up) has a retry of its OWN, on EVERY read — the anonymous one, the visitor's,
//   a preview's, the base one (`refusedSignups` / `runSiteRead`): refused, the same read is asked again without it and
//   nothing else is lost; absent means the block shows no "Who signed up" buttons — the safe side. ⚠ Not on the
//   slot-people retry: that one drops custom CSS, white-label branding and page access with it, and the visitor's read
//   has no base twin, so a guest on a guests-only page would have stayed locked out until the gateway was recomposed.
/**
 * #145 — who signed up for a sign-up list item. Asked for on every read; dropped only by its own retry. #149's
 * `soldOnline` rides the same retry: refused, it is absent — the item then opens the free sign-up, which the server
 * refuses for a paid item with a message the guest reads (never a free claim on something the host charges for). The
 * `quantityTaken` count (owner, 2026-10-11) rides it too: refused, the block shows the total offered, as before.
 */
const SIGNUPS_SELECTION = 'signups { guestKey name quantity note } soldOnline quantityTaken';

/**
 * ROADMAP B25 / B26 — the organizer credit with the profile's PUBLIC contact facts (the organizer footer block), and the
 * organizer's other upcoming events (the organizer-events block). Asked for on EVERY read; an older gateway that refuses
 * them is asked again without them (`runSiteRead`) — absent, both blocks render nothing, the safe side.
 */
/**
 * #165 — every upcoming session an act plays (the artists block shows a recurring event's NEXT date from them). Its own
 * retry (`refusedArtistSets`): refused by a gateway not yet recomposed, the read is asked again without it — the block
 * then shows every act, as before. Never a reason to lose the page.
 */
const ARTIST_SETS_SELECTION = "sets { slotId startsAt endsAt stage }";

/** B18 / B19 — how the hero arrives, whether the main button nudges, the page effect; its own retry (`refusedHeroMotion`). */
const HERO_MOTION_SELECTION = "entrance ctaNudge pageEffect coverEntrance opener";

const ORGANIZER_SELECTION =
  'organizer { id slug name summary logoUploadedFile { fileUrl thumbnailUrl } website email phone socialUrls placeName streetAddress city stateProvince postalCode country }';
const ORGANIZER_EVENTS_SELECTION = `organizerEvents { ${ORGANIZER_EVENT_FIELDS} } organizerChannel`;

/**
 * #204 step 4 — the event's causes for the Donate / Goal blocks: the organizer page's own cause fields (one list), plus
 * whether gifts can be taken here now and whether they take test cards. Its own retry (`refusedEventCauses`): refused by a
 * gateway not yet recomposed, the read is asked again without it — the two blocks then show nothing to give to.
 */
const EVENT_CAUSES_SELECTION = `causes { ${PUBLIC_CAUSE_FIELD_LIST} } causesCanGive causesTestMode`;

const SLOT_PEOPLE_FIELDS = `
              descriptionFormat
              track
              speakers {
                id
                name
                guestRole
                bio
                website
                socialLinks
                avatarUploadedFile { fileUrl thumbnailUrl }
                gravatarUrl
              }
              acts {
                id
                name
                kind
                billing
                photoUploadedFile { fileUrl thumbnailUrl }
                website
                spotifyUrl
                youtubeUrl
                socials
              }`;

/** A team member's sessions (#70) — asked for under the same retry as the schedule's people. */
const GUEST_SESSION_FIELDS = `
              sessions { id name startsAt endsAt isAllDay track }`;

const publicEventSiteQuery = (withPreview: boolean, withSlotPeople = true, withVisitor = false, withWhen = true) => gql`
  query PublicEventSite($params: PublicEventSiteInput) {
    publicEventSite(params: $params) {
      code
      errors { name errors }
      data {
        url
        initial
        ${withPreview ? 'isPreview' : ''}
        ${withPreview && withSlotPeople ? 'crewView' : ''}
        ${withVisitor ? `pages { ${PAGE_NAV_FIELDS} ${PAGE_ACCESS_FIELDS} ${withWhen ? PAGE_WHEN_FIELDS : ''} }` : ''}
        event {
          ${EVENT_SHELL_FIELDS}
          pages { ${PAGE_NAV_FIELDS} ${withSlotPeople ? PAGE_ACCESS_FIELDS : ''} ${withSlotPeople && withWhen ? PAGE_WHEN_FIELDS : ''} }
          highlights { ${HIGHLIGHT_FIELDS} }
          theme { id name mode tokens customCss }
          hero { fullScreen showDetails videoAutoplay videoMuted videoLoop ${HERO_MOTION_SELECTION} }
          rsvpButtonLabel
          dmMode
          rsvpDeadline
          rsvpReminderDays
          doorsOpenAt
          scheduleMode
          placesLeft
          upcomingDates { id name label startsAt endsAt isAllDay doorsOpenAt }
          upcomingDatesTotal
          messageCopy { surface trigger statusKey bodyHtml }
          ${ORGANIZER_SELECTION}
          ${withSlotPeople ? 'searchIndexable hasRestrictedPages privateSite' : ''}
          ${withSlotPeople && withWhen ? 'visibilityChangesAt hidePlatformBranding customCss' : ''}
        }
        page {
          meta { ${PAGE_NAV_FIELDS} ${withSlotPeople ? PAGE_ACCESS_FIELDS : ''} ${withSlotPeople && withWhen ? PAGE_WHEN_FIELDS : ''} }
          widgets { ${WIDGET_FIELDS} ${withSlotPeople && withWhen ? PAGE_WHEN_FIELDS : ''} }
          data {
            timeSlots {
              id
              name
              description
              startsAt
              endsAt
              isAllDay
              venueName
              venueAddress
              venuePhone
              venueWebsite
              venueDirectionsUrl
              ${withSlotPeople ? SLOT_PEOPLE_FIELDS : ''}
            }
            guests {
              id
              name
              guestRole
              rsvpStatus
              bio
              website
              socialLinks
              avatarUploadedFile { fileUrl thumbnailUrl }
              gravatarUrl
              groupIds
              jobTitle
              company
              ${withSlotPeople ? GUEST_SESSION_FIELDS : ''}
            }
            pois {
              id name description categoryId categoryName icon color
              address link website phone email price currency priceUnit upvoteCount
            }
            accommodations {
              id name description categoryId categoryName icon color
              images { url uploadedFile { fileUrl thumbnailUrl } }
              address distanceMeters link website phone email price currency priceUnit groupBookingCode upvoteCount
            }
            travel {
              id travelMode travelStatus direction departsAt arrivesAt
              fromCode fromLabel fromTimezone toCode toLabel toTimezone carrier serviceNumber
              travellers { name seat }
            }
            mapElements {
              id name description categoryId categoryName icon color hoursText latitude longitude
              sessions { startsAt title track }
            }
            signupItems {
              id name description categoryId categoryName icon color
              images { url uploadedFile { fileUrl thumbnailUrl } }
              link price currency priceUnit quantityAvailable maxQuantityPerGuest upvoteCount
              ${SIGNUPS_SELECTION}
            }
            pollQuestions {
              id question description categoryId categoryName icon color
              questionType options { id label } isQuiz allowAddOption isRequired
              images { url uploadedFile { fileUrl thumbnailUrl } }
            }
            tickets {
              id name description isAddOn price currency isPayWhatYouWant minPrice maxPrice
              quantityTotal available soldOut salesStartsAt salesEndsAt salesMode orderNo
              membershipMode membershipTypeNames
              priceRules { ruleKind effectKind amount orderNo startsAt endsAt membershipTypeId membershipName }
            }
            placesLeft
            statCounts { attending guests }
            raceResults {
              id bibNumber participantName groupRef groupLabel startedAt quitAt durationMs
            }
            budget {
              id name categoryId categoryName icon color calcType
              expected paid remaining pledged dueDate responsibleLabel currency
            }
            budgetCurrency
            tasks {
              id title taskType description categoryId categoryName icon color
              scheduledAt location taskStatus expectedDurationMin actualDurationSec
              responsibleKind responsibleLabel
            }
            partners {
              id name tierLabel kind partnerPackageId
              logoUploadedFile { fileUrl thumbnailUrl }
              logoGravatarUrl
              websiteUrl socials
            }
            partnerPackages {
              id partnerPackageId name partnerKind
              publicSummary publicSummaryFormat benefits
              amount serviceFeeAmount totalAmount serviceFeeToSponsor cardFeeToSponsor feeMode currencyCode
              slotsLeft isFull isOpenForApplications
              applicationsCloseAt paymentMode
            }
            volunteerShifts {
              id name volunteerRoleId roleName roleSummary roleResponsibilities roleEligibility roleColor roleIcon
              startsAt endsAt locationKind locationLabel latitude longitude
              quantityNeeded spotsLeft isFull approvalMode applicationsCloseAt
              rewards { name description color icon quantity }
            }
            artists {
              id name kind billing billingIndex blurb
              photoUploadedFile { fileUrl thumbnailUrl }
              website spotifyUrl youtubeUrl socials
              setTimeStartsAt setTimeEndsAt stage
              ${ARTIST_SETS_SELECTION}
            }
            prizeDraws {
              id name description drawn pendingMessage
              winners { displayName prize orderNo }
            }
            gameScoreboards {
              gameId gameName roundNo total inPlay out
              teams { team inPlay out total }
              eliminations { displayName markerLabel markerColor team outcome }
              checklist
              networking
              whoami
              leaderboard { displayName done total complete rank identityName }
              pairs { members }
            }
            ${ORGANIZER_EVENTS_SELECTION}
            ${EVENT_CAUSES_SELECTION}
          }
        }
      }
    }
  }
`;

const PUBLIC_EVENT_SITE_QUERY = publicEventSiteQuery(false);
const PUBLIC_EVENT_SITE_PREVIEW_QUERY = publicEventSiteQuery(true);
const PUBLIC_EVENT_SITE_QUERY_BASE = publicEventSiteQuery(false, false);
const PUBLIC_EVENT_SITE_PREVIEW_QUERY_BASE = publicEventSiteQuery(true, false);
// Guests-only pages — the BROWSER's second read, naming its visitor. No base twin: a gateway that does not know the
// argument refuses it, and the page simply stays as the anonymous read left it (locked) — never the other way.
const PUBLIC_EVENT_SITE_VISITOR_QUERY = publicEventSiteQuery(false, true, true);
const PUBLIC_EVENT_SITE_PREVIEW_VISITOR_QUERY = publicEventSiteQuery(true, true, true);
// #92 — the same shapes without the time fields, for a gateway that does not know them yet.
const PUBLIC_EVENT_SITE_QUERY_NO_WHEN = publicEventSiteQuery(false, true, false, false);
const PUBLIC_EVENT_SITE_PREVIEW_QUERY_NO_WHEN = publicEventSiteQuery(true, true, false, false);
const PUBLIC_EVENT_SITE_VISITOR_QUERY_NO_WHEN = publicEventSiteQuery(false, true, true, false);
const PUBLIC_EVENT_SITE_PREVIEW_VISITOR_QUERY_NO_WHEN = publicEventSiteQuery(true, true, true, false);

/**
 * The gateway refused the query because it does not know #92's time fields yet — nothing else. ⓘ #96's
 * `hidePlatformBranding` rides this retry: refused, it is absent, and absent means the promo SHOWS — the safe side; and
 * #97's `customCss`: absent means no custom CSS.
 */
export const refusedWhenFields = (result: OpResult): boolean =>
  !result.didSucceed() &&
  /Cannot query field ["']closesAt["'] on type ["'](PublicEventPageMeta|PublicPageWidget)["']|Cannot query field ["'](visibilityChangesAt|hidePlatformBranding|customCss)["'] on type ["']PublicEvent["']/.test(
    errorMessages(result).join("\n")
  );

/** #145 — the gateway refused the query because it does not know a sign-up item's `signups` yet — nothing else. */
export const refusedSignups = (result: OpResult): boolean =>
  !result.didSucceed() && /Cannot query field ["'](signups|soldOnline|quantityTaken)["'] on type ["']PublicEventSignupItem["']/.test(errorMessages(result).join("\n"));

/** The same read without `signups` — built once per query. */
const noSignupsTwins = new Map<unknown, ReturnType<typeof gql>>();
const withoutSignups = (query: ReturnType<typeof gql>): ReturnType<typeof gql> => {
  let twin = noSignupsTwins.get(query);

  if (!twin) {
    twin = gql(String(query.loc?.source?.body ?? "").replace(SIGNUPS_SELECTION, ""));
    noSignupsTwins.set(query, twin);
  }

  return twin;
};

/** ROADMAP B25 / B26 — the gateway refused the query because it does not know the organizer's new fields yet — nothing else. */
export const refusedOrganizerExtras = (result: OpResult): boolean =>
  !result.didSucceed() &&
  /Cannot query field ["'](website|email|phone|socialUrls|placeName|streetAddress|city|stateProvince|postalCode|country)["'] on type ["']PublicEventOrganizer["']|Cannot query field ["'](organizerEvents|organizerChannel)["'] on type ["']PublicPageData["']/.test(
    errorMessages(result).join("\n")
  );

/** The same read without the organizer's contact facts and events — built once per query. */
const noOrganizerExtrasTwins = new Map<unknown, ReturnType<typeof gql>>();
const withoutOrganizerExtras = (query: ReturnType<typeof gql>): ReturnType<typeof gql> => {
  let twin = noOrganizerExtrasTwins.get(query);

  if (!twin) {
    twin = gql(String(query.loc?.source?.body ?? "").replace(ORGANIZER_SELECTION, "").replace(ORGANIZER_EVENTS_SELECTION, ""));
    noOrganizerExtrasTwins.set(query, twin);
  }

  return twin;
};

/** B18 / B19 — the gateway refused the query because it does not know the hero's motion fields yet — nothing else. */
export const refusedHeroMotion = (result: OpResult): boolean =>
  !result.didSucceed() && /Cannot query field ["'](entrance|ctaNudge|pageEffect|coverEntrance|opener)["'] on type ["']PublicEventHero["']/.test(errorMessages(result).join("\n"));

/** The same read without the hero's motion fields (the site then shows no entrance and no nudge) — built once per query. */
const noHeroMotionTwins = new Map<unknown, ReturnType<typeof gql>>();
const withoutHeroMotion = (query: ReturnType<typeof gql>): ReturnType<typeof gql> => {
  let twin = noHeroMotionTwins.get(query);

  if (!twin) {
    twin = gql(String(query.loc?.source?.body ?? "").replace(` ${HERO_MOTION_SELECTION} }`, " }"));
    noHeroMotionTwins.set(query, twin);
  }

  return twin;
};

/** #165 — the gateway refused the query because it does not know an act's `sets` yet — nothing else. */
export const refusedArtistSets = (result: OpResult): boolean =>
  !result.didSucceed() && /Cannot query field ["']sets["'] on type ["']PublicEventArtist["']/.test(errorMessages(result).join("\n"));

/** The same read without the acts' sets — built once per query. */
const noArtistSetsTwins = new Map<unknown, ReturnType<typeof gql>>();
const withoutArtistSets = (query: ReturnType<typeof gql>): ReturnType<typeof gql> => {
  let twin = noArtistSetsTwins.get(query);

  if (!twin) {
    twin = gql(String(query.loc?.source?.body ?? "").replace(ARTIST_SETS_SELECTION, ""));
    noArtistSetsTwins.set(query, twin);
  }

  return twin;
};

/** #204 step 4 — the gateway refused the query because it does not know the event's causes yet — nothing else. */
export const refusedEventCauses = (result: OpResult): boolean =>
  !result.didSucceed() &&
  /Cannot query field ["'](causes|causesCanGive|causesTestMode)["'] on type ["']PublicPageData["']|on type ["']PublicCause(Donor)?["']|Unknown type ["']PublicCause/.test(
    errorMessages(result).join("\n")
  );

/** The same read without the event's causes — built once per query. */
const noEventCausesTwins = new Map<unknown, ReturnType<typeof gql>>();
const withoutEventCauses = (query: ReturnType<typeof gql>): ReturnType<typeof gql> => {
  let twin = noEventCausesTwins.get(query);

  if (!twin) {
    twin = gql(String(query.loc?.source?.body ?? "").replace(EVENT_CAUSES_SELECTION, ""));
    noEventCausesTwins.set(query, twin);
  }

  return twin;
};

/**
 * Run a page read. Refused for the organizer's new fields alone (B25 / B26), ask again without them; then for the event's
 * causes (#204 step 4); then for the hero's motion fields (B18 / B19); then for an act's `sets` (#165); refused for
 * `signups` alone, ask again without it (#145) — the answer goes on down the chain.
 */
const runSiteRead = async (query: ReturnType<typeof gql>, variables: Record<string, unknown>, opts: Record<string, unknown>): Promise<OpResult> => {
  let asked = query;
  let result = await ApiService.executeQuery(asked, variables, opts);

  if (refusedOrganizerExtras(result)) {
    asked = withoutOrganizerExtras(asked);
    result = await ApiService.executeQuery(asked, variables, opts);
  }

  if (refusedEventCauses(result)) {
    asked = withoutEventCauses(asked);
    result = await ApiService.executeQuery(asked, variables, opts);
  }

  if (refusedHeroMotion(result)) {
    asked = withoutHeroMotion(asked);
    result = await ApiService.executeQuery(asked, variables, opts);
  }

  if (refusedArtistSets(result)) {
    asked = withoutArtistSets(asked);
    result = await ApiService.executeQuery(asked, variables, opts);
  }

  return refusedSignups(result) ? ApiService.executeQuery(withoutSignups(asked), variables, opts) : result;
};

/** The gateway refused the query because it does not know a session's new fields yet (not recomposed) — nothing else. */
export const refusedSlotPeople = (result: OpResult): boolean =>
  !result.didSucceed() &&
  /Cannot query field ["'](descriptionFormat|speakers|track|acts)["'] on type ["']PublicEventTimeSlot["']|Cannot query field ["']sessions["'] on type ["']PublicEventGuest["']|Cannot query field ["'](searchIndexable|hasRestrictedPages|privateSite)["'] on type ["']PublicEvent["']|Cannot query field ["'](locked|hiddenBlocks)["'] on type ["']PublicEventPageMeta["']|Cannot query field ["']crewView["'] on type ["']PublicEventSite["']/.test(
    errorMessages(result).join("\n")
  );

/**
 * Public, read-only reads for the event website. Runs against whatever GraphQL
 * client each app injects via `configureApiService` (SSR client on the server,
 * browser client for lazy block hydration).
 */
/**
 * #243 c — the API left the guests out (`deferGuests`): say so on the page's data when it has a people block (its
 * `guests` is a list), so the site reads them after load and the block shows they are coming meanwhile.
 */
export const markGuestsDeferred = (result: OpResult): OpResult => {
  const site = result.didSucceed() && Array.isArray(result.data) ? (result.data[0] as PublicEventSite | undefined) : undefined;

  if (site?.page?.data && Array.isArray(site.page.data.guests)) {
    site.page.data.guestsDeferred = true;
  }

  return result;
};

export class PublicEventService {
  /**
   * Resolve an event-website URL in a single call. Pass the full incoming URL
   * and `initial`:
   *   • initial=true  → `data[0].event` (shell + nav pages) AND `data[0].page`
   *   • initial=false → `data[0].page` only (`data[0].event` is null)
   * `data` is an array of 0 or 1 items — empty when no published event/page
   * matched the url (render a 404). Defaults to a cache-friendly fetch policy;
   * override per call for fresh SSR reads.
   */
  static async getEventSite(
    args: PublicEventSiteArgs,
    options: PublicReadOptions = {}
  ): Promise<OpResult> {
    const preview = (args.preview ?? "").trim();
    const visitorId = (args.visitorId ?? "").trim();
    const inviteToken = (args.inviteToken ?? "").trim();
    const deferGuests = !!args.deferGuests && !preview && !visitorId && !inviteToken;
    const variables = {
      params: {
        url: args.url,
        initial: !!args.initial,
        ...(preview ? { preview } : {}),
        ...(visitorId ? { visitorId } : {}),
        ...(inviteToken ? { inviteToken } : {}),
        ...(deferGuests ? { deferGuests: true } : {}),
      },
    };
    const opts = { fetchPolicy: "cache-first" as const, ...options };

    if (visitorId) {
      const visitorResult = await runSiteRead(preview ? PUBLIC_EVENT_SITE_PREVIEW_VISITOR_QUERY : PUBLIC_EVENT_SITE_VISITOR_QUERY, variables, opts);

      return refusedWhenFields(visitorResult)
        ? runSiteRead(preview ? PUBLIC_EVENT_SITE_PREVIEW_VISITOR_QUERY_NO_WHEN : PUBLIC_EVENT_SITE_VISITOR_QUERY_NO_WHEN, variables, opts)
        : visitorResult;
    }

    let fullQuery = preview ? PUBLIC_EVENT_SITE_PREVIEW_QUERY : PUBLIC_EVENT_SITE_QUERY;
    let params: Record<string, unknown> = variables.params;
    let result = await runSiteRead(fullQuery, { params }, opts);

    if (inviteToken && refusedNewInputs(result, ["inviteToken"])) {
      const { inviteToken: _dropped, ...older } = variables.params;
      params = older;
      result = await runSiteRead(fullQuery, { params }, opts);
    }

    // #243 c — a gateway that predates `deferGuests` refuses it: asked again without, the guests come as before.
    let guestsDeferred = deferGuests;

    if (deferGuests && refusedNewInputs(result, ["deferGuests"])) {
      const { deferGuests: _dropped, ...older } = params;
      params = older;
      guestsDeferred = false;
      result = await runSiteRead(fullQuery, { params }, opts);
    }

    if (refusedWhenFields(result)) {
      fullQuery = preview ? PUBLIC_EVENT_SITE_PREVIEW_QUERY_NO_WHEN : PUBLIC_EVENT_SITE_QUERY_NO_WHEN;
      result = await runSiteRead(fullQuery, { params }, opts);
    }

    // ⓘ With the params that last got through (an invitation the gateway refused stays out of the base read too).
    const answer = refusedSlotPeople(result)
      ? await runSiteRead(preview ? PUBLIC_EVENT_SITE_PREVIEW_QUERY_BASE : PUBLIC_EVENT_SITE_QUERY_BASE, { params }, opts)
      : result;

    return guestsDeferred ? markGuestsDeferred(answer) : answer;
  }
}
